import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation } from "react-router";
import { RouteFactory } from "@app/routePaths";
import { useFetchArticle } from "@routes/Article/hooks/useFetchArticle";
import { ArticlePaidModeType } from "@routes/Article/utils/enums";
import { fetchData } from "@routes/Article/utils/helpers";
import {
  FeedArticleData,
  FeedNextSectionArticlesUrlPaths,
} from "@routes/Article/utils/types";
import { tryCatch } from "@typings/TryCatch";
import {
  ARTICLE_INIFINITE_SCROLL_LIMIT,
  PODCAST_ARTICLE_INIFINITE_SCROLL_LIMIT,
} from "@util/constant";
import { checkIfFromPaidPressRelease } from "@util/helpers";
import { includes } from "lodash-es";

type UseNextArticleProps = {
  data: FeedArticleData;
  lang?: string;
  isPodcastAudioArticle?: boolean;
  /** Variant for A/B Test */
  variant?: string;
  /** Feature flag name for A/B Test */
  featureFlagName?: string;
};

export const useNextArticle = ({
  data,
  lang,
  isPodcastAudioArticle = false,
  variant,
  featureFlagName,
}: UseNextArticleProps) => {
  const { pathname } = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [moreStories, setMoreStories] = useState<FeedArticleData[]>([data]);

  const [moreStoriesUrlPaths, setMoreStoriesUrlPaths] = useState<
    FeedNextSectionArticlesUrlPaths[]
  >([]);
  const [tempFirstArticle, setTempFirstArticle] =
    useState<FeedArticleData | null>(null);

  const checkIfFetchMoreArticles = useCallback(() => {
    if (
      isPodcastAudioArticle &&
      moreStories.length >= PODCAST_ARTICLE_INIFINITE_SCROLL_LIMIT
    )
      return;
    if (typeof window === "undefined") return;
    if (!data) return;

    if (pathname.startsWith(RouteFactory.aseanBusiness)) return;
    if (pathname.startsWith(RouteFactory.globalEnterprise)) return;
    const userAgent = window.navigator.userAgent;
    if (includes(userAgent.toLowerCase(), "googlebot")) return;
    // Check if the article is from a paid press release.
    const isPaidPressRelease = checkIfFromPaidPressRelease(data?.sections);
    if (isPaidPressRelease) return;
    if (data.isPreview) return;

    return true;
  }, [pathname, data, isPodcastAudioArticle, moreStories.length]);

  useEffect(() => {
    if (!data) return;
    if (moreStories.length > 1) return;
    if (data?.paidMode !== ArticlePaidModeType.PREMIUM) return;

    if (tempFirstArticle) return;

    const FetchFullContent = async () => {
      setIsLoading(true);

      const urlPath = data.urlPath.replace("/zh-hans", "");
      const article = await useFetchArticle({
        urlPath,
        lang,
      });

      setIsLoading(false);

      if (typeof article === "undefined") return;

      setMoreStories([article]);
      setTempFirstArticle(article);
    };

    FetchFullContent();
  }, [data, moreStories, tempFirstArticle, lang]);

  useEffect(() => {
    if (!checkIfFetchMoreArticles()) return;

    if (moreStoriesUrlPaths.length > 0) return;
    if (moreStories.length > 1) return;

    const fetchMoreUrlPaths = async () => {
      const request = fetchData(isPodcastAudioArticle, data);
      const { data: fetchedData, error } = await tryCatch(request);

      if (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching next section articles:", error);

        return;
      }

      if (!fetchedData) return;

      setMoreStoriesUrlPaths(fetchedData);
    };

    fetchMoreUrlPaths();
  }, [
    data,
    moreStoriesUrlPaths,
    moreStories,
    variant,
    featureFlagName,
    checkIfFetchMoreArticles,
    isPodcastAudioArticle,
  ]);

  const FetchNextArticle = async () => {
    setIsLoading(true);

    const articleIndex = moreStories.length - 1;
    const urlPath = moreStoriesUrlPaths?.[articleIndex]?.urlPath;
    const article = await useFetchArticle({ urlPath, lang });

    setIsLoading(false);

    if (typeof article === "undefined") return;

    setMoreStories((prev) => [...prev, article]);
  };

  const { ref } = useInView({
    triggerOnce: true,
    onChange: async (inView) => {
      if (!inView) return;
      if (!checkIfFetchMoreArticles()) return;
      if (moreStories.length >= ARTICLE_INIFINITE_SCROLL_LIMIT) return;
      if (moreStoriesUrlPaths.length === 0) return;

      FetchNextArticle();
    },
  });

  return {
    moreStories,
    isLoading,
    ref,
    moreStoriesUrlPaths,
  };
};
